// src/pages/Employment/EditEmployment.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  FaArrowLeft,
  FaBuilding,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaBriefcase,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaGlobe,
  FaCalendarAlt,
  FaCheckCircle,
  FaSpinner,
  FaSave,
  FaTimes,
} from 'react-icons/fa';
import { successToast, errorToast } from '../../Utils/toast';
import {
  useGetAdminJobQuery,
  useUpdateJobMutation,
} from '../../redux/services/employmentService/employment.Services';

function EditEmployment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading: isFetching } = useGetAdminJobQuery(id);
  const [updateJob, { isLoading: isUpdating }] = useUpdateJobMutation();

  const [formData, setFormData] = useState({
    title: '',
    category: 'job',
    type: 'full_time',
    description: '',
    requirements: '',
    responsibilities: '',
    location: '',
    salary_range: '',
    experience_required: '',
    qualification: '',
    company_name: '',
    company_website: '',
    company_email: '',
    contact_person: '',
    contact_phone: '',
    email: '',
    phone: '',
    whatsapp: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    application_deadline: '',
    is_featured: false,
    is_verified: false,
    status: 'pending',
  });

  const [logoFile, setLogoFile] = useState(null);
  const [previewLogo, setPreviewLogo] = useState(null);
  const [existingLogo, setExistingLogo] = useState(null);

  useEffect(() => {
    if (data?.data) {
      const job = data.data;
      setFormData({
        title: job.title || '',
        category: job.category || 'job',
        type: job.type || 'full_time',
        description: job.description || '',
        requirements: job.requirements || '',
        responsibilities: job.responsibilities || '',
        location: job.location || '',
        salary_range: job.salary_range || '',
        experience_required: job.experience_required || '',
        qualification: job.qualification || '',
        company_name: job.company_name || '',
        company_website: job.company_website || '',
        company_email: job.company_email || '',
        contact_person: job.contact_person || '',
        contact_phone: job.contact_phone || '',
        email: job.email || '',
        phone: job.phone || '',
        whatsapp: job.whatsapp || '',
        address: job.address || '',
        city: job.city || '',
        state: job.state || '',
        country: job.country || 'India',
        pincode: job.pincode || '',
        application_deadline: job.application_deadline ? new Date(job.application_deadline).toISOString().slice(0, 10) : '',
        is_featured: job.is_featured === 1 || job.is_featured === true,
        is_verified: job.is_verified === 1 || job.is_verified === true,
        status: job.status || 'pending',
      });
      setExistingLogo(job.company_logo || null);
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewLogo(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setPreviewLogo(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      errorToast('Job title is required');
      return;
    }

    if (!formData.description.trim()) {
      errorToast('Description is required');
      return;
    }

    if (!formData.company_name.trim()) {
      errorToast('Company name is required');
      return;
    }

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        formDataToSend.append(key, formData[key]);
      });
      if (logoFile) {
        formDataToSend.append('logo', logoFile);
      }

      const response = await updateJob({ id, data: formDataToSend }).unwrap();
      successToast(response?.message || 'Job updated successfully');
      navigate('/employment');
    } catch (error) {
      console.error('Update job error:', error);
      errorToast(error?.data?.message || 'Failed to update job');
    }
  };

  if (isFetching) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h5 className="mt-3">Loading job details...</h5>
      </div>
    );
  }

  return (
    <div>
      {/* HEADER */}
      <div className="page-header-breadcrumb all_breadcrumb">
        <div className="ms-md-1 mb-1 mb-md-0 ms-0 page_heading_breadcrumb">
          <nav>
            <h4 className="fw-semibold">
              <Link to="/employment" className="text-primary me-2">
                <FaArrowLeft />
              </Link>
              Edit Employment / Business
            </h4>
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item active">
                <Link to="/">Home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/employment">Employment</Link>
              </li>
              <li className="breadcrumb-item">Edit</li>
            </ol>
          </nav>
        </div>
        <div className="page-title fw-semibold fs-18 mb-0">
          <button
            className="btn btn-secondary me-2"
            onClick={() => navigate('/employment')}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isUpdating}
          >
            {isUpdating ? (
              <>
                <FaSpinner className="spinning me-2" />
                Updating...
              </>
            ) : (
              <>
                <FaSave className="me-1" />
                Update Job
              </>
            )}
          </button>
        </div>
      </div>

      {/* FORM */}
      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Basic Information */}
              <div className="col-12">
                <h5 className="fw-bold mb-3">Basic Information</h5>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label>
                    Job Title <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Enter job title"
                    required
                  />
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group mb-3">
                  <label>
                    Category <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                  >
                    <option value="job">Job</option>
                    <option value="business">Business</option>
                    <option value="internship">Internship</option>
                    <option value="freelance">Freelance</option>
                    <option value="consultancy">Consultancy</option>
                  </select>
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group mb-3">
                  <label>
                    Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                  >
                    <option value="full_time">Full Time</option>
                    <option value="part_time">Part Time</option>
                    <option value="contract">Contract</option>
                    <option value="remote">Remote</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
              </div>

              <div className="col-12">
                <div className="form-group mb-3">
                  <label>
                    Description <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter job description"
                    rows="4"
                    required
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label>Requirements</label>
                  <textarea
                    className="form-control"
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleChange}
                    placeholder="Enter job requirements"
                    rows="3"
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label>Responsibilities</label>
                  <textarea
                    className="form-control"
                    name="responsibilities"
                    value={formData.responsibilities}
                    onChange={handleChange}
                    placeholder="Enter job responsibilities"
                    rows="3"
                  />
                </div>
              </div>

              {/* Company Information - Same as Create page */}
              {/* ... (same fields as CreateEmployment) ... */}

              {/* Submit Button */}
              <div className="col-12 mt-4">
                <div className="form-group">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg w-100"
                    disabled={isUpdating}
                  >
                    {isUpdating ? (
                      <>
                        <FaSpinner className="spinning me-2" />
                        Updating Job...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Update Job
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditEmployment;